import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Input, FormFeedback } from 'reactstrap';
import BoolInput from '../../../../../misc/BoolInput';
import EnumInput from '../../../../../misc/EnumInput';
import NullableIntInputCheckboxText from '../../../../../misc/NullableIntInputCheckboxText';
import UserSyncReportRecipients from '../Shared/UserSyncReportRecipients';
import EntraIdUserEmailAddressSettings from './EntraIdUserEmailAddressSettings';
import EntraIdUserPasswordSettings from './EntraIdUserPasswordSettings';
import EntraIdUserUserPrincipalNameSettings from './EntraIdUserUserPrincipalNameSettings';
import { CoreEntraIdUserExportConfiguration, EntraIdUserExportCompanyNameSource, EntraIdUserExportDepartmentSource, EntraIdUserExportPasswordSettingType, EntraIdUserExportPrimaryEmailAddressSource, EntraIdUserExportUserPrincipalNameSource, EntraIdUserExportUserType } from '../../../../../../models/CoreEntraIdUserModels';
import * as EntraIdUserExportTypes from './EntraIdUserTypes';

const bitCount = (i: number) => {
    i = i - ((i >> 1) & 0x55555555);
    i = (i & 0x33333333) + ((i >> 2) & 0x33333333);
    i = (i + (i >> 4)) & 0x0F0F0F0F;
    return (i * 0x01010101) >> 24;
}

export const validator = (configuration: CoreEntraIdUserExportConfiguration) => {
    const validation: string[] = [];

    if (configuration.synchronizedUserType !== EntraIdUserExportUserType.STUDENT
        &&
        configuration.synchronizedUserType !== EntraIdUserExportUserType.STAFF) {
        validation.push('synchronizedUserType')
    }
    if (configuration.companyNameSource === EntraIdUserExportCompanyNameSource.CONSTANT_VALUE
        &&
        !configuration.companyNameConstant) {
        validation.push('companyNameConstant');
    }
    if (configuration.departmentSource === EntraIdUserExportDepartmentSource.CONSTANT_VALUE
        &&
        !configuration.departmentConstant) {
        validation.push('departmentConstant');
    }

    if ((configuration.archiveGracePeriod === undefined) || (configuration.archiveGracePeriod !== null && configuration.archiveGracePeriod! < 0)) {
        validation.push('archiveGracePeriod')
    }
    if (configuration.archiveGracePeriod === 0 && (configuration.deleteGracePeriod !== null && configuration.deleteGracePeriod < 14)) {
        validation.push('deleteGracePeriodDays')
    }
    if (configuration.deleteGracePeriod === undefined || (configuration.deleteGracePeriod !== null && configuration.deleteGracePeriod! < 0)) {
        validation.push('deleteGracePeriod')
    }

    if (configuration.passwordSettings.type === EntraIdUserExportPasswordSettingType.RANDOM) {
        if (configuration.passwordSettings.minLength < 8) {
            validation.push('passwordSettings.minLength');
        };
        if (configuration.passwordSettings.maxLength < configuration.passwordSettings.minLength) {
            validation.push('passwordSettings.maxLength');
        };
        if (bitCount(configuration.passwordSettings.passwordCharacterSets) < 2) {
            validation.push('passwordSettings.passwordCharacterSets');
        };
    };
    if (configuration.passwordSettings.type === EntraIdUserExportPasswordSettingType.STATIC) {
        if ((!configuration.passwordSettings.useMonthAndDayFromSocialnumber && configuration.passwordSettings.staticValue.length < 8) ||
            (configuration.passwordSettings.useMonthAndDayFromSocialnumber && configuration.passwordSettings.staticValue.length < 4)) {
            validation.push('passwordSettings.staticValue');
        };
    };

    if (configuration.primaryEmailAddressSettings.forceDomainName &&
        !configuration.primaryEmailAddressSettings.forceDomainNameValue) {
        validation.push('primaryEmailAddressSettings.forceDomainNameValue');
    };

    if (configuration.primaryEmailAddressSettings.source === EntraIdUserExportPrimaryEmailAddressSource.GENERATED &&
        !configuration.primaryEmailAddressSettings.forceDomainName) {
        validation.push('primaryEmailAddressSettings.forceDomainName');
    };

    if (configuration.userPrincipalNameSettings.forceDomainName &&
        !configuration.userPrincipalNameSettings.forceDomainNameValue) {
        validation.push('userPrincipalNameSettings.forceDomainNameValue');
    };

    if (configuration.userPrincipalNameSettings.source === EntraIdUserExportUserPrincipalNameSource.GENERATED &&
        !configuration.userPrincipalNameSettings.forceDomainName) {
        validation.push('userPrincipalNameSettings.forceDomainName');
    };

    return validation;
}


interface EntraIdUserExportConfigurationProps {
    configuration: CoreEntraIdUserExportConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreEntraIdUserExportConfiguration>) => void
    onValidChange: (arg: boolean) => void 
}


const EntraIdUserExportConfiguration = (props: EntraIdUserExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration]);

    const updateLifeCycleManagement = (value: number | null | undefined) => {
        if (value === null) {
            return props.updatePart({ archiveGracePeriod: value, deleteGracePeriod: null });
        }
        props.updatePart({ archiveGracePeriod: value });
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.EntraIdUserExport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <EnumInput
                            title={t('entraIdUserExport.synchronizedUserType.title', { ns: 'configuration' })}
                            name='synchronizedUserType'
                            value={props.configuration.synchronizedUserType}
                            options={EntraIdUserExportTypes.userTypeOptions.map(key => ({
                                value: key,
                                text: t(`entraIdUserExport.synchronizedUserType.options.${key}`, { ns: 'configuration' })
                            }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('synchronizedUserType') !== -1}
                            invalidFeedback={t('entraIdUserExport.synchronizedUserType.invalidFeedback', { ns: 'configuration' })}
                        />

                        <FormGroup>
                            <h5>{t('entraIdUserExport.companyNameSource.header', { ns: 'configuration' })}</h5>
                            <EnumInput
                                title={t('entraIdUserExport.companyNameSource.title', { ns: 'configuration' })}
                                titleSmall={true}
                                name='companyNameSource'
                                value={props.configuration.companyNameSource}
                                options={EntraIdUserExportTypes.companyNameSourceOptions.map(key => ({
                                    value: key,
                                    text: t(`entraIdUserExport.companyNameSource.options.${key}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('companyNameSource') !== -1}
                                invalidFeedback={t('entraIdUserExport.companyNameSource.invalidFeedback', { ns: 'configuration' })}
                            />
                            <Input
                                type="text"
                                className="ms-3 md-w-50 lg-w-25"
                                name="companyNameConstant"
                                id="companyNameConstant"
                                value={props.configuration.companyNameConstant || ''}
                                disabled={props.disabled || props.configuration.companyNameSource !== EntraIdUserExportCompanyNameSource.CONSTANT_VALUE}
                                onChange={(e) => props.updatePart({ companyNameConstant: e.target.value })}
                                invalid={validation.indexOf('companyNameConstant') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback className="ms-3">{t('entraIdUserExport.companyNameSource.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('entraIdUserExport.departmentSource.header', { ns: 'configuration' })}</h5>
                            <EnumInput
                                title={t('entraIdUserExport.departmentSource.title', { ns: 'configuration' })}
                                titleSmall={true}
                                name='departmentSource'
                                value={props.configuration.departmentSource}
                                options={EntraIdUserExportTypes.departmentNameSourceOptions.map(key => ({
                                    value: key,
                                    text: t(`entraIdUserExport.departmentSource.options.${key}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('departmentSource') !== -1}
                                invalidFeedback={t('entraIdUserExport.departmentSource.invalidFeedback', { ns: 'configuration' })}
                            />
                            <Input
                                type="text"
                                className="ms-3 md-w-50 lg-w-25"
                                name="departmentConstant"
                                id="departmentConstant"
                                value={props.configuration.departmentConstant || ''}
                                disabled={props.disabled || props.configuration.departmentSource !== EntraIdUserExportDepartmentSource.CONSTANT_VALUE}
                                onChange={(e) => props.updatePart({ departmentConstant: e.target.value })}
                                invalid={validation.indexOf('departmentConstant') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback className="ms-3">{t('entraIdUserExport.departmentSource.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('entraIdUserExport.employeeIdSource.header', { ns: 'configuration' })}</h5>
                            <EnumInput
                                title={t('entraIdUserExport.employeeIdSource.title', { ns: 'configuration' })}
                                titleSmall={true}
                                name='employeeIdSource'
                                value={props.configuration.employeeIdSource}
                                options={EntraIdUserExportTypes.employeeIdSourceOptions.map(key => ({
                                    value: key,
                                    text: t(`entraIdUserExport.employeeIdSource.options.${key}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('employeeIdSource') !== -1}
                                invalidFeedback={t('entraIdUserExport.employeeIdSource.invalidFeedback', { ns: 'configuration' })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('entraIdUserExport.employeeTypeSource.header', { ns: 'configuration' })}</h5>
                            <EnumInput
                                title={t('entraIdUserExport.employeeTypeSource.title', { ns: 'configuration' })}
                                titleSmall={true}
                                name='employeeTypeSource'
                                value={props.configuration.employeeTypeSource}
                                options={EntraIdUserExportTypes.employeeTypeSourceOptions.map(key => ({
                                    value: key,
                                    text: t(`entraIdUserExport.employeeTypeSource.options.${key}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('employeeTypeSource') !== -1}
                                invalidFeedback={t('entraIdUserExport.employeeTypeSource.invalidFeedback', { ns: 'configuration' })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('entraIdUserExport.jobTitleSource.header', { ns: 'configuration' })}</h5>
                            <EnumInput
                                title={t('entraIdUserExport.jobTitleSource.title', { ns: 'configuration' })}
                                titleSmall={true}
                                name='jobTitleSource'
                                value={props.configuration.jobTitleSource}
                                options={EntraIdUserExportTypes.jobTitleSourceOptions.map(key => ({
                                    value: key,
                                    text: t(`entraIdUserExport.jobTitleSource.options.${key}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                                invalid={validation.indexOf('jobTitleSource') !== -1}
                                invalidFeedback={t('entraIdUserExport.jobTitleSource.invalidFeedback', { ns: 'configuration' })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <BoolInput
                                title={t('entraIdUserExport.exportAddress.title', { ns: 'configuration' })}
                                value={props.configuration.exportAddress}
                                name="exportAddress"
                                updateValue={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                            />
                        </FormGroup>

                        <EnumInput
                            title={t('entraIdUserExport.extensionsSource.title', { ns: 'configuration' })}
                            value={props.configuration.extensionsSource}
                            options={EntraIdUserExportTypes.EntraIdUserExportExtensionsSourceOptions.map(key => ({
                                value: key,
                                text: t(`entraIdUserExport.extensionsSource.options.${key}`, { ns: 'configuration' })
                            }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            name='extensionsSource'
                            multi={true}
                        />

                        <EntraIdUserPasswordSettings
                            passwordSettings={props.configuration.passwordSettings}
                            onChange={x => props.updatePart({ passwordSettings: x })}
                            disabled={props.disabled}
                            validation={validation}
                        />

                        <EntraIdUserUserPrincipalNameSettings
                            userPrincipalNameSettings={props.configuration.userPrincipalNameSettings}
                            disabled={props.disabled}
                            onChange={x => props.updatePart({ userPrincipalNameSettings: x })}
                            validation={validation}
                        />

                        <EntraIdUserEmailAddressSettings
                            disabled={props.disabled}
                            onChange={x => props.updatePart({ primaryEmailAddressSettings: x })}
                            primaryEmailAddressSettings={props.configuration.primaryEmailAddressSettings}
                            validation={validation}
                        />

                        <FormGroup>
                            <h5>{t('entraIdUserExport.gracePeriod.header', { ns: 'configuration' })}</h5>
                            <NullableIntInputCheckboxText
                                name="archiveGracePeriod"
                                value={props.configuration.archiveGracePeriod}
                                disabled={props.disabled}
                                title={t('entraIdUserExport.gracePeriod.archiveGracePeriod.title', { ns: 'configuration' })}
                                invalidFeedback={t('entraIdUserExport.gracePeriod.archiveGracePeriod.invalidFeedback', { ns: 'configuration' })}
                                invalid={validation.indexOf('archiveGracePeriod') !== -1}
                                defaultValue={0}
                                onChange={(name, value) => updateLifeCycleManagement( value )}
                                checkboxTitle={t('entraIdUserExport.gracePeriod.archiveGracePeriod.checkboxTitle', { ns: 'configuration' })}
                            />
                            <NullableIntInputCheckboxText
                                name="deleteGracePeriod"
                                value={props.configuration.deleteGracePeriod}
                                disabled={props.disabled || props.configuration.archiveGracePeriod === null}
                                title={t('entraIdUserExport.gracePeriod.deleteGracePeriod.title', { ns: 'configuration' })}
                                invalidFeedback={validation.indexOf('deleteGracePeriod') !== -1 ?
                                    t('entraIdUserExport.gracePeriod.deleteGracePeriod.invalid', { ns: 'configuration' })
                                    : validation.indexOf('deleteGracePeriodDays') !== -1 ?
                                        t('entraIdUserExport.gracePeriod.deleteGracePeriod.invalidDays', {ns: 'configuration' }) : ''
                                }
                                defaultValue={365}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                checkboxTitle={t('entraIdUserExport.gracePeriod.deleteGracePeriod.checkboxTitle', { ns: 'configuration' })}
                                invalid={validation.indexOf('deleteGracePeriod') !== -1 || validation.indexOf('deleteGracePeriodDays') !== -1}
                            />
                        </FormGroup>

                        <UserSyncReportRecipients
                            reportRecipients={props.configuration.reportRecipients}
                            disableInput={props.disabled}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                        />
                    </div>
                </CardBody>
            </Collapse>
        </Card>
    );
}

export default EntraIdUserExportConfiguration;
